import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
  Alert,
  AlertActionCloseButton,
  AlertGroup,
  AlertVariant,
} from '@patternfly/react-core';
import './NotificationContext.css';

export interface Notification {
  id: string;
  title: string;
  message?: string;
  variant: AlertVariant;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  notifyError: (title: string, message?: string) => void;
  notifySuccess: (title: string, message?: string) => void;
  notifyWarning: (title: string, message?: string) => void;
  notifyInfo: (title: string, message?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NOTIFICATION_TIMEOUT = 5000; // 5 seconds

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id'>) => {
      const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newNotification: Notification = { ...notification, id };

      setNotifications((prev) => [...prev, newNotification]);

      // Auto-remove after timeout
      setTimeout(() => {
        removeNotification(id);
      }, NOTIFICATION_TIMEOUT);
    },
    [removeNotification]
  );

  const notifyError = useCallback(
    (title: string, message?: string) => {
      addNotification({ title, message, variant: AlertVariant.danger });
    },
    [addNotification]
  );

  const notifySuccess = useCallback(
    (title: string, message?: string) => {
      addNotification({ title, message, variant: AlertVariant.success });
    },
    [addNotification]
  );

  const notifyWarning = useCallback(
    (title: string, message?: string) => {
      addNotification({ title, message, variant: AlertVariant.warning });
    },
    [addNotification]
  );

  const notifyInfo = useCallback(
    (title: string, message?: string) => {
      addNotification({ title, message, variant: AlertVariant.info });
    },
    [addNotification]
  );

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    notifyError,
    notifySuccess,
    notifyWarning,
    notifyInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <AlertGroup isToast isLiveRegion className="notification-alert-group">
        {notifications.map((notification) => (
          <Alert
            key={notification.id}
            variant={notification.variant}
            title={notification.title}
            isPlain
            isInline
            actionClose={
              <AlertActionCloseButton
                title={notification.title}
                onClose={() => removeNotification(notification.id)}
              />
            }
            timeout={NOTIFICATION_TIMEOUT}
            onTimeout={() => removeNotification(notification.id)}
          >
            {notification.message}
          </Alert>
        ))}
      </AlertGroup>
    </NotificationContext.Provider>
  );
};
